import React, { useState, useEffect } from "react";
import {
  getAllGuests,
  addGuest,
  getGuestById,
  updateGuest,
  deleteGuest,
  getByMobileNumber,
} from "../../helpers/api.ts";
import Swal from "sweetalert2";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Button,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  FormFeedback,
  Spinner,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb.js";
import { Link } from "react-router-dom";

const Guest = () => {
  const [guests, setGuests] = useState([]);
  const [modal, setModal] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    middle_name: "",
    gender: "",
    date_of_birth: "",
    mobile_number: "",
    email: "",
    vehicle_number: "",
    address: "",
    zip_code: "",
    nationality: "",
    arrive_from: "",
    departure_to: "",
    country: "",
    state: "",
    city: "",
    purpose_of_visit: "",
    business_source: "",
    company_name: "",
    pax_guests: "",
    profile_image: null,
  });

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  let hotel_id1;
  if (
    user?.role === "RECEPTIONIST" ||
    user?.role === "SPA" ||
    user?.role === "FOOD AND BEVERAGE"
  ) {
    hotel_id1 = user?.hotel?.hotel_id;
  } else {
    hotel_id1 = user?.hotel_id;
  }

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    search: "",
    nationality: "",
    gender: "",
  });

  const [paginationInfo, setPaginationInfo] = useState({
    total: 0,
    currentPage: 1,
    totalPages: 1,
  });

  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchGuests();
  }, [pagination]);

  // ✅ FIXED: Email is now optional — removed from required fields
  const validateForm = () => {
    const newErrors = {};

    // Required fields validation
    if (!(formData.first_name || "").trim())
      newErrors.first_name = "First name is required";
    if (!(formData.last_name || "").trim())
      newErrors.last_name = "Last name is required";
    if (!formData.gender) newErrors.gender = "Gender is required";
    if (!formData.date_of_birth)
      newErrors.date_of_birth = "Date of birth is required";

    // Mobile number validation
    if (!formData.mobile_number) {
      newErrors.mobile_number = "Mobile number is required";
    } else if (!/^\d{10}$/.test(formData.mobile_number)) {
      newErrors.mobile_number = "Mobile number must be 10 digits";
    }

    // ✅ FIXED: Email validation — only validate format IF email is provided (not required)
    if (formData.email && formData.email.trim() !== "") {
      if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = "Invalid email format";
      }
    }
    // ✅ No "email is required" check — email is now optional

    // Age validation
    if (
      formData.age &&
      (isNaN(formData.age) || formData.age < 0 || formData.age > 120)
    ) {
      newErrors.age = "Please enter a valid age between 0 and 120";
    }

    // Zip code validation
    if (formData.zip_code && !/^\d{6}$/.test(formData.zip_code)) {
      newErrors.zip_code = "Zip code must be 6 digits";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const searchByMobile = async (mobileNumber) => {
    try {
      setLoading(true);
      const formDataToSend = {};
      const response = await getByMobileNumber(mobileNumber, formDataToSend);
      if (response.success) {
        setSelectedGuest(response.data);
        setFormData(response.data);
        setModal(true);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Not Found",
        text: "No guest found with this mobile number",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchGuests = async () => {
    try {
      setTableLoading(true);
      const response = await getAllGuests({ pagination, hotel_id: hotel_id1 });
      setGuests(response.data.guests);
      setPaginationInfo(response.data.pagination);
    } catch (error) {
      console.error("Error fetching guests:", error);
      setGuests([]);
    } finally {
      setTableLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "profile_image") {
      setFormData({
        ...formData,
        [name]: files[0],
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      const formDataToSend = new FormData();
      Object.keys(formData).forEach((key) => {
        formDataToSend.append(key, formData[key]);
      });

      formDataToSend.append("hotel_id", hotel_id1);

      if (selectedGuest) {
        await updateGuest(selectedGuest.guest_id.toString(), formDataToSend);
        Swal.fire({
          icon: "success",
          title: "Updated!",
          text: "Guest information has been updated successfully",
        });
      } else {
        await addGuest(formDataToSend);
        Swal.fire({
          icon: "success",
          title: "Added!",
          text: "New guest has been added successfully",
        });
      }

      setModal(false);
      resetForm();
      fetchGuests();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Something went wrong!";
      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (id) => {
    try {
      const response = await getGuestById(id, {});
      if (response.success) {
        setSelectedGuest(response.data);
        setFormData(response.data);
        setModal(true);
      }
    } catch (error) {
      console.error("Error fetching guest details:", error);
    }
  };

  const handleDelete = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteGuest(id);
          Swal.fire("Deleted!", "Guest has been deleted.", "success");
          fetchGuests();
        } catch (error) {
          Swal.fire("Error!", "Failed to delete guest.", "error");
        }
      }
    });
  };

  const resetForm = () => {
    setSelectedGuest(null);
    setFormData({
      first_name: "",
      last_name: "",
      middle_name: "",
      age: "",
      gender: "",
      date_of_birth: "",
      mobile_number: "",
      email: "",
      vehicle_number: "",
      address: "",
      zip_code: "",
      nationality: "",
      arrive_from: "",
      departure_to: "",
      country: "",
      state: "",
      city: "",
      purpose_of_visit: "",
      business_source: "",
      company_name: "",
      pax_guests: "",
      profile_image: null,
    });
  };

  const handleNameSearch = (searchTerm) => {
    const filteredGuests = guests.filter((guest) => {
      const fullName =
        `${guest.first_name} ${guest.middle_name} ${guest.last_name}`.toLowerCase();
      return fullName.includes(searchTerm.toLowerCase());
    });
    setGuests(filteredGuests);
  };

  return (
    <Container fluid className="page-content">
      <Breadcrumbs title="Guest" breadcrumbItem="Guest Management" />
      <Row>
        <Col>
          <Card>
            <CardBody>
              <div className="d-flex justify-content-end align-items-end mb-2">
                <Button
                  color="primary"
                  onClick={() => {
                    setModal(true);
                    resetForm();
                  }}
                  className="w-100 w-md-auto"
                >
                  Add New Guest
                </Button>
              </div>

              {/* Search and Filter Section */}
              <Row className="mb-3 g-2">
                <Col xs={12} md={6}>
                  <Input
                    type="search"
                    placeholder="Search by mobile number..."
                    onChange={(e) => {
                      if (e.target.value.length >= 10) {
                        searchByMobile(e.target.value);
                      }
                    }}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <Input
                    type="search"
                    placeholder="Search by guest name..."
                    onChange={(e) => {
                      const searchTerm = e.target.value;
                      if (searchTerm) {
                        handleNameSearch(searchTerm);
                      } else {
                        fetchGuests();
                      }
                    }}
                  />
                </Col>
              </Row>

              {/* Guests Table */}
              {tableLoading ? (
                <div className="text-center my-5">
                  <Spinner color="primary" />
                </div>
              ) : (
                <div className="table-responsive">
                  <Table striped bordered hover>
                    <thead>
                      <tr>
                        <th>Sr. No</th>
                        <th>Name</th>
                        <th className="d-none d-md-table-cell">Mobile</th>
                        <th className="d-none d-lg-table-cell">Email</th>
                        <th className="d-none d-md-table-cell">Nationality</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {guests.map((guest, i) => (
                        <tr key={guest.guest_id}>
                          <td>{i + 1}</td>
                          <td>
                            <div className="d-flex flex-column">
                              <span>{`${guest.first_name} ${guest.last_name}`}</span>
                              <small className="text-muted d-md-none">{guest.mobile_number}</small>
                            </div>
                          </td>
                          <td className="d-none d-md-table-cell">{guest.mobile_number}</td>
                          <td className="d-none d-lg-table-cell">{guest.email}</td>
                          <td className="d-none d-md-table-cell">{guest.nationality}</td>
                          <td>
                            <div className="d-flex flex-column flex-md-row gap-2">
                              <Button
                                color="info"
                                size="sm"
                                className="flex-grow-1 flex-md-grow-0"
                                onClick={() => handleEdit(guest.guest_id)}
                              >
                                Edit
                              </Button>
                              <Button
                                color="primary"
                                size="sm"
                                className="flex-grow-1 flex-md-grow-0"
                                tag={Link}
                                to={`/guest/${guest.guest_id}`}
                              >
                                View
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Add/Edit Guest Modal */}
      <Modal isOpen={modal} toggle={() => setModal(!modal)} size="lg" className="modal-fullscreen-md-down">
        <ModalHeader toggle={() => setModal(!modal)}>
          {selectedGuest ? "Edit Guest" : "Add New Guest"}
        </ModalHeader>
        <ModalBody>
          <Form onSubmit={handleSubmit}>
            <Row className="g-2">
              <Col xs={12} md={4}>
                <FormGroup>
                  <Label>First Name*</Label>
                  <Input
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    invalid={!!errors.first_name}
                  />
                  <FormFeedback>{errors.first_name}</FormFeedback>
                </FormGroup>
              </Col>
              <Col xs={12} md={4}>
                <FormGroup>
                  <Label>Middle Name</Label>
                  <Input
                    name="middle_name"
                    value={formData.middle_name}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </Col>
              <Col xs={12} md={4}>
                <FormGroup>
                  <Label>Last Name*</Label>
                  <Input
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    invalid={!!errors.last_name}
                  />
                  <FormFeedback>{errors.last_name}</FormFeedback>
                </FormGroup>
              </Col>
            </Row>

            <Row className="g-2">
              <Col xs={12} md={4}>
                <FormGroup>
                  <Label>Age</Label>
                  <Input
                    type="number"
                    name="age"
                    value={formData.age}
                    onChange={handleInputChange}
                    invalid={!!errors.age}
                  />
                  <FormFeedback>{errors.age}</FormFeedback>
                </FormGroup>
              </Col>
              <Col xs={12} md={4}>
                <FormGroup>
                  <Label>Gender*</Label>
                  <Input
                    type="select"
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    invalid={!!errors.gender}
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </Input>
                  <FormFeedback>{errors.gender}</FormFeedback>
                </FormGroup>
              </Col>
              <Col xs={12} md={4}>
                <FormGroup>
                  <Label>Date of Birth*</Label>
                  <Input
                    type="date"
                    name="date_of_birth"
                    value={formData.date_of_birth}
                    onChange={handleInputChange}
                    invalid={!!errors.date_of_birth}
                  />
                  <FormFeedback>{errors.date_of_birth}</FormFeedback>
                </FormGroup>
              </Col>
            </Row>

            <Row className="g-2">
              <Col xs={12} md={6}>
                <FormGroup>
                  <Label>Mobile Number*</Label>
                  <Input
                    name="mobile_number"
                    value={formData.mobile_number}
                    onChange={handleInputChange}
                    invalid={!!errors.mobile_number}
                    maxLength={10}
                  />
                  <FormFeedback>{errors.mobile_number}</FormFeedback>
                </FormGroup>
              </Col>
              {/* ✅ FIXED: Email is now optional — label changed, no * required indicator */}
              <Col xs={12} md={6}>
                <FormGroup>
                  <Label>Email <small className="text-muted">(Optional)</small></Label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    invalid={!!errors.email}
                    placeholder="Enter email address (optional)"
                  />
                  {/* ✅ Only shows error if invalid format is entered */}
                  <FormFeedback>{errors.email}</FormFeedback>
                </FormGroup>
              </Col>
            </Row>

            <Row className="g-2">
              <Col xs={12} md={6}>
                <FormGroup>
                  <Label>Vehicle Number</Label>
                  <Input
                    name="vehicle_number"
                    value={formData.vehicle_number}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </Col>
              <Col xs={12} md={6}>
                <FormGroup>
                  <Label>Zip Code</Label>
                  <Input
                    name="zip_code"
                    value={formData.zip_code}
                    onChange={handleInputChange}
                    invalid={!!errors.zip_code}
                  />
                  <FormFeedback>{errors.zip_code}</FormFeedback>
                </FormGroup>
              </Col>
            </Row>

            <FormGroup>
              <Label>Address</Label>
              <Input
                type="textarea"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
              />
            </FormGroup>

            <Row className="g-2">
              <Col xs={12} md={4}>
                <FormGroup>
                  <Label>Country</Label>
                  <Input
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </Col>
              <Col xs={12} md={4}>
                <FormGroup>
                  <Label>State</Label>
                  <Input
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </Col>
              <Col xs={12} md={4}>
                <FormGroup>
                  <Label>City</Label>
                  <Input
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row className="g-2">
              <Col xs={12} md={6}>
                <FormGroup>
                  <Label>Nationality</Label>
                  <Input
                    name="nationality"
                    value={formData.nationality}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </Col>
              <Col xs={12} md={6}>
                <FormGroup>
                  <Label>Purpose of Visit</Label>
                  <Input
                    name="purpose_of_visit"
                    value={formData.purpose_of_visit}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row className="g-2">
              <Col xs={12} md={6}>
                <FormGroup>
                  <Label>Arrive From</Label>
                  <Input
                    name="arrive_from"
                    value={formData.arrive_from}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </Col>
              <Col xs={12} md={6}>
                <FormGroup>
                  <Label>Departure To</Label>
                  <Input
                    name="departure_to"
                    value={formData.departure_to}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row className="g-2">
              <Col xs={12} md={4}>
                <FormGroup>
                  <Label>Business Source</Label>
                  <Input
                    name="business_source"
                    value={formData.business_source}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </Col>
              <Col xs={12} md={4}>
                <FormGroup>
                  <Label>Company Name</Label>
                  <Input
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </Col>
              <Col xs={12} md={4}>
                <FormGroup>
                  <Label>Pax Guests</Label>
                  <Input
                    name="pax_guests"
                    value={formData.pax_guests}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </Col>
            </Row>

            <div className="d-flex justify-content-end mt-4">
              <Button type="submit" color="primary" disabled={loading} className="w-100 w-md-auto">
                {loading ? (
                  <>
                    <Spinner size="sm" /> {" Loading..."}
                  </>
                ) : selectedGuest ? (
                  "Update Guest"
                ) : (
                  "Add Guest"
                )}
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>
    </Container>
  );
};

export default Guest;